import axios, { AxiosInstance, AxiosResponse } from 'axios';

// Types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: {
    username: string;
    role: string;
  };
}

export interface RegisterRequest {
  username: string;
  password: string;
}

export interface ImageUploadData {
  username: string;
  age?: number;
  body_part?: string;
  diameter_larger_than_pencil?: boolean;
  geolocation_city?: string;
  geolocation_region?: string;
  geolocation_lat?: number;
  geolocation_lon?: number;
  additional_comment?: string;
  timestamp?: string;
}

export interface ImageRecord {
  id: string;
  username: string;
  timestamp: string;
  image_b64: string;
  segmentation_result: any;
  first_classification: string;
  fallback_result?: any;
  segmented_image_b64: string;
  advance_processed: boolean;
  age?: number;
  body_part?: string;
  diameter_larger_than_pencil?: boolean;
  geolocation_city?: string;
  geolocation_region?: string;
  geolocation_lat?: number;
  geolocation_lon?: number;
  additional_comment?: string;
}

export interface ProcessRequest {
  username: string;
  body_part?: string;
  image_id?: string;
}

export interface AnalysisItem {
  asymmetry: string;
  border_irregularity: string;
  color_variegation: string;
  diameter_assessment: string;
  segmentation_analysis: string;
  extra_info_influence: string;
  other_diagnoses: string;
  image_technical_commentaries: string;
  evolution_assessment: string;
  advance_classification: string;
  confidence_level: number;
  final_explanation: string;
}

export interface AdvancedAnalysisOutput {
  analysis: AnalysisItem;
  overall_recommendation: string;
}

export interface PendingImage {
  id: string;
  timestamp: string;
  body_part?: string;
  first_classification?: string;
}

export interface DashboardStats {
  total_users: number;
  total_images: number;
  total_analyses: number;
  body_part_distribution: Array<{ _id: string; count: number }>;
  risk_distribution: Array<{ _id: string; count: number }>;
}

export interface UserActivity {
  daily_uploads: Array<{ _id: string; uploads: number }>;
  daily_analyses: Array<{ _id: string; analyses: number }>;
}

export interface SystemHealth {
  health_score: number;
  health_status: 'excellent' | 'good' | 'warning' | 'critical';
  database_health: string;
  system_metrics: {
    total_users: number;
    total_images: number;
    total_analyses: number;
    recent_uploads_24h: number;
    recent_analyses_24h: number;
  };
  performance_metrics: {
    error_rate: number;
    avg_processing_time: number;
    storage_usage_percent: number;
  };
  issues: string[];
}

export interface DetailedAnalytics {
  user_analytics: Array<{
    username: string;
    total_uploads: number;
    unique_body_parts: number;
    avg_age: number;
    first_upload: string;
    last_upload: string;
    days_active: number;
  }>;
  geographic_distribution: Array<{
    _id: { city: string; region: string };
    count: number;
  }>;
  age_demographics: Array<{
    _id: string | number;
    count: number;
  }>;
  risk_trends: Array<{
    _id: { date: string; classification: string };
    count: number;
  }>;
  confidence_analysis: {
    avg_confidence: number;
    min_confidence: number;
    max_confidence: number;
    high_confidence_count: number;
    low_confidence_count: number;
    total_analyses: number;
  };
  summary: {
    total_active_users: number;
    avg_uploads_per_user: number;
    most_active_user: string;
    geographic_coverage: number;
  };
}

export interface PerformanceMetrics {
  daily_metrics: Array<{
    _id: string;
    analyses_count: number;
    avg_confidence: number;
    high_risk_count: number;
    processing_errors: number;
  }>;
  body_part_frequency: Array<{
    _id: string;
    count: number;
    avg_confidence: number;
  }>;
  classification_distribution: Array<{
    _id: string;
    count: number;
    avg_confidence: number;
  }>;
  system_alerts: Array<{
    type: string;
    message: string;
    timestamp: string;
    severity: string;
  }>;
  performance_summary: {
    total_analyses: number;
    avg_daily_volume: number;
    overall_confidence: number;
    error_rate: number;
  };
}

export interface AdminUser {
  username: string;
  role: string;
  created_at: string;
  total_uploads: number;
  total_analyses: number;
  last_activity?: string;
  status: 'active' | 'inactive';
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string | null) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  // Authentication
  async login(username: string, password: string): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/login', {
      username,
      password,
    });
    return response.data;
  }

  async register(username: string, password: string, role: string = 'user'): Promise<{ msg: string }> {
    const response = await this.api.post('/register', {
      username,
      password,
      role,
    });
    return response.data;
  }

  // Image upload
  async uploadImage(file: File, data: ImageUploadData): Promise<ImageRecord> {
    const formData = new FormData();
    formData.append('file', file);
    
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    const response: AxiosResponse<ImageRecord> = await this.api.post('/upload_image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // Longer timeout for image upload
    });
    return response.data;
  }

  // Analysis
  async startProcess(data: ProcessRequest): Promise<AdvancedAnalysisOutput> {
    const response: AxiosResponse<AdvancedAnalysisOutput> = await this.api.post('/start_process', data, {
      timeout: 120000, // Longer timeout for analysis
    });
    return response.data;
  }

  async getPendingAnalysis(): Promise<PendingImage[]> {
    const response: AxiosResponse<PendingImage[]> = await this.api.get('/pending_analysis');
    return response.data;
  }

  async listAnalyses(params?: {
    start_date?: string;
    end_date?: string;
    body_part?: string;
  }): Promise<any[]> {
    const response = await this.api.get('/list_analyses', { params });
    return response.data;
  }

  // Dashboard
  async getDashboardStats(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<DashboardStats> {
    const response: AxiosResponse<DashboardStats> = await this.api.get('/dashboard/stats', { params });
    return response.data;
  }

  async getUserActivity(days: number = 30): Promise<UserActivity> {
    const response: AxiosResponse<UserActivity> = await this.api.get('/dashboard/user_activity', {
      params: { days },
    });
    return response.data;
  }

  // Image management
  async getSegmentedImage(imageId: string): Promise<{ segmented_image_b64: string }> {
    const response = await this.api.get(`/get_segmented_image/${imageId}`);
    return response.data;
  }

  async deleteImage(imageId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/delete_image/${imageId}`);
    return response.data;
  }

  async deleteAnalysis(analysisId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/delete_analysis/${analysisId}`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    const response = await this.api.get('/healthz');
    return response.data;
  }

  // Advanced Analytics
  async getSystemHealth(): Promise<SystemHealth> {
    const response: AxiosResponse<SystemHealth> = await this.api.get('/analytics/system_health');
    return response.data;
  }

  async getDetailedAnalytics(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<DetailedAnalytics> {
    const response: AxiosResponse<DetailedAnalytics> = await this.api.get('/analytics/detailed_stats', { params });
    return response.data;
  }

  async getPerformanceMetrics(days: number = 30): Promise<PerformanceMetrics> {
    const response: AxiosResponse<PerformanceMetrics> = await this.api.get('/analytics/performance_metrics', {
      params: { days },
    });
    return response.data;
  }

  async getUserManagementData(): Promise<{ users: AdminUser[]; summary: any }> {
    const response = await this.api.get('/analytics/user_management');
    return response.data;
  }

  async exportAnalyticsData(params?: {
    format?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<any> {
    const response = await this.api.get('/analytics/export_data', { params });
    return response.data;
  }

  // Admin User Management
  async getAllUsersAdmin(): Promise<{ users: AdminUser[] }> {
    const response = await this.api.get('/admin/users');
    return response.data;
  }

  async updateUserRole(username: string, newRole: string): Promise<{ message: string }> {
    const response = await this.api.put(`/admin/users/${username}/role`, { new_role: newRole });
    return response.data;
  }

  async deleteUserAdmin(username: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/admin/users/${username}`);
    return response.data;
  }

  // System Settings
  async getSystemSettings(): Promise<any> {
    const response = await this.api.get('/admin/settings');
    return response.data;
  }

  async updateSystemSettings(settings: any): Promise<{ message: string; updated_settings: any }> {
    const response = await this.api.put('/admin/settings', settings);
    return response.data;
  }

  async getSystemLogs(params?: {
    limit?: number;
    level?: string;
  }): Promise<{ logs: any[]; total_count: number; filter_level: string }> {
    const response = await this.api.get('/admin/system_logs', { params });
    return response.data;
  }
}

export const apiService = new ApiService();
